import { useEffect, useMemo, useRef, useState } from 'react';
import { mapsApi } from '../../api/maps';
import { decodePolyline } from '../../lib/polyline';
import { createTrack } from './motion';
import { addPlaces } from './places/layer';
import { addVehicleImages, vehicleImageId } from './vehicleSprites';
import './inGoMap.css';

/**
 * A map drawn from InGo's own map service (the mobile plan's M8; AGENTS.md
 * rule 7: MapLibre, keyless tiles, no Google): the style comes from
 * `GET maps/config`, so moving to the deployment's own tiles is one setting.
 * Cut from the console's `src/maps/JourneyTraceMap.tsx` (KangaruRide's
 * `TripTraceMap`): MapLibre loaded only by a screen that draws a map, the
 * credit kept findable (OpenStreetMap's licence), colours from the design
 * tokens read at draw time.
 *
 * Draws the pins (pickup, drop-off), the road between them when given, the
 * rider when given, and the phone's own position (`me`) when given, and keeps
 * them in view. With `onPinMoved` the pins can be dragged to fine-tune them
 * (the client's request screen). Where the phone cannot draw a map (no WebGL2,
 * the map service down) it says so in a line, and the screen around it still
 * has the addresses (the mobile plan's fallback).
 *
 * Drawn as Kangaru's clean map (Rio, 2 Oct 2026: "a clean map screan as we do
 * for the kangaru"; KangaruRide `OrderMap.tsx`, ADR-0091):
 * - the basemap quietened: buildings, house numbers and residential shading
 *   removed, the ground white, roads and water pale;
 * - the road on a white casing; pickup a ring, drop-off a solid dot; the
 *   phone's own dot in a soft halo;
 * - no zoom buttons (one finger pans, two zoom), the credit folded to its (i);
 * - one round recentre button whenever there is something to frame;
 * - `bleed` for a map that fills the width: square corners, not a card.
 *
 * @param {{
 *   pins: Array<{ role: 'pickup' | 'dropoff', lat: number, lng: number }>,
 *   route?: string | null,
 *   rider?: { lat: number, lng: number } | null,
 *   me?: { lat: number, lng: number } | null,
 *   onPinMoved?: (role: 'pickup' | 'dropoff', lat: number, lng: number) => void,
 *   label: string,
 *   height?: number | string,
 *   bleed?: boolean,
 *   vehicles?: Array<{ key: string, vehicle: string, state?: string, lat: number, lng: number, heading?: number | null, at?: number }> | null,
 *   focusZoom?: number,
 *   meVehicle?: { vehicle: string, state?: string } | null,
 *   riderVehicle?: { vehicle: string, state?: string } | null,
 * }} props
 *
 * `vehicles` are riders drawn as InGo's own vehicles (`vehicleSprites.js`),
 * each gliding on its own track and turning to its heading. They never move
 * the camera: a person may be looking around. `focusZoom` is the zoom one
 * point is framed at. `meVehicle` draws the phone's own position as the
 * rider's own vehicle, and `riderVehicle` draws `rider` as theirs (blue, on a
 * delivery, unless its state says otherwise), with the same glide, instead of
 * a dot; both turn to the position's `heading`.
 */
export default function InGoMap({ pins, route = null, rider = null, me = null, onPinMoved, label, height = 220, bleed = false, vehicles = null, focusZoom = 15, meVehicle = null, riderVehicle = null }) {
  const container = useRef(null);
  const map = useRef(null);
  const lib = useRef(null);
  const markers = useRef({});
  // The rider glides between polled positions (M7, `motion.js`) instead of jumping.
  const track = useRef(createTrack());
  const frame = useRef(0);
  const moved = useRef(onPinMoved);
  moved.current = onPinMoved;
  const framed = useRef('');
  // What the recentre button returns to: everything drawn, as last framed.
  const frameOn = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [canRecentre, setCanRecentre] = useState(false);
  // The vehicles: one track and one turn per key, and their own frame loop.
  const fleet = useRef(new Map());
  const fleetFrame = useRef(0);
  const [fleetReady, setFleetReady] = useState(false);
  // Everything drawn as a vehicle: the riders asked for, the phone's own, the rider on a delivery.
  // A map with neither reads no position here, so a parent's new `me` each render costs nothing.
  const meKind = meVehicle?.vehicle ?? null;
  const meState = meVehicle?.state ?? 'free';
  const riderKind = riderVehicle?.vehicle ?? null;
  const riderState = riderVehicle?.state ?? 'delivering';
  const meShown = meKind ? me : null;
  const riderShown = riderKind ? rider : null;
  const drawn = useMemo(() => {
    if (vehicles === null && !meKind && !riderKind) return null;
    const list = [...(vehicles || [])];
    if (meShown) list.push({ key: 'me', vehicle: meKind, state: meState, lat: meShown.lat, lng: meShown.lng, heading: meShown.heading ?? null, at: meShown.at });
    if (riderShown) list.push({ key: 'rider', vehicle: riderKind, state: riderState, lat: riderShown.lat, lng: riderShown.lng, heading: riderShown.heading ?? null, at: riderShown.at });

    return list;
  }, [vehicles, meKind, meState, meShown, riderKind, riderState, riderShown]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([mapsApi.mapConfig(), loadMapLibre()])
      .then(([config, maplibre]) => {
        if (cancelled || !container.current) return;
        const first = pins[0] || me;
        const instance = new maplibre.Map({
          container: container.current,
          style: config.style_url,
          center: first ? [first.lng, first.lat] : config.centre ? [config.centre.longitude, config.centre.latitude] : [0, 0],
          zoom: 12,
          attributionControl: false,
          // At most twice the CSS pixels: a full-screen map on a dense phone
          // otherwise asks the GPU for a canvas several times the size, which an
          // emulated or modest GPU never fills (the navigation map, 2 Oct 2026).
          pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
        });
        instance.addControl(new maplibre.AttributionControl({ compact: true, customAttribution: config.attribution }), 'bottom-left');
        instance.on('load', () => {
          if (cancelled) return;
          try {
            quieten(instance);
            foldCredit(container.current);
          } catch (error) {
            // The look is a nicety: the pins, the road and the rider are not.
            console.warn(`[InGoMap] could not quieten the basemap: ${error?.message || error}`);
          }
          // InGo's places, the console's twenty kinds, under the street names
          // (the live-map plan): a nicety too, never in the way of the rest.
          // `data-places` says they are drawn, for a check.
          addPlaces(instance, { visible: true, text: paletteColour('--ingo-muted', '#5b6472'), halo: '#ffffff' }).then(
            () => {
              if (!cancelled && container.current) container.current.dataset.places = '1';
            },
            (error) => !cancelled && console.warn(`[InGoMap] the places could not be drawn: ${error?.message || error}`),
          );
          // A seam a check can read: the map drew its first tiles (MapLibre's
          // 'load'). A blank map never gets it, though it has a canvas.
          if (container.current) container.current.dataset.ready = '1';
          // And how many riders it draws: none yet. The vehicles' drawings may
          // still be decoding, and a missing count is not a zero (ingo-0d's
          // device run, 3 Oct 2026: a ready client home with no rider near read
          // no count at all). The vehicles' effect keeps it from here.
          if (container.current && container.current.dataset.vehicles === undefined) container.current.dataset.vehicles = '0';
          setReady(true);
        });
        // The credit is written in after the first tiles: fold it once more then.
        instance.once('idle', () => !cancelled && foldCredit(container.current));
        // One string, so it reads in a phone's log too (a WebView prints an object as [object Object]).
        instance.on('error', (e) => console.warn(`[InGoMap] ${e?.error?.message || 'map error'}${e?.sourceId ? ` (source ${e.sourceId})` : ''}`));
        instance.on('webglcontextlost', () => console.warn('[InGoMap] the WebGL context was lost: the map stops drawing'));
        lib.current = maplibre;
        map.current = instance;
      })
      .catch((error) => {
        console.warn(`[InGoMap] no map: ${error?.message || error}`);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      Object.values(markers.current).forEach((m) => m.remove());
      markers.current = {};
      map.current?.remove();
      map.current = null;
      framed.current = '';
      setReady(false);
    };
    // The map is made once per screen; what it shows follows below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draggable = typeof onPinMoved === 'function';

  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance) return;

    const road = route ? decodePolyline(route) : [];
    const colour = (name, fallback) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
    const pickupColour = colour('--ingo-success', '#1a8f4a');
    const dropoffColour = colour('--ingo-danger', '#c62828');
    const show = (id, data, layer) => {
      const source = instance.getSource(id);
      if (source) source.setData(data);
      else {
        instance.addSource(id, { type: 'geojson', data });
        instance.addLayer({ id, source: id, ...layer });
      }
    };
    const dot = (point) => ({ type: 'FeatureCollection', features: point ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [point.lng, point.lat] } }] : [] });

    const roadData = { type: 'FeatureCollection', features: road.length >= 2 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: road } }] : [] };
    // The road on a white casing (Kangaru's route: white 9 under the line 5).
    show('ingo-route-casing', roadData, { type: 'line', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': 9 } });
    show('ingo-route', roadData, { type: 'line', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': colour('--ingo-secondary', '#07408f'), 'line-width': 5 } });

    // Fixed pins are drawn on the map itself; pins a person may drag are markers.
    // The pickup is a ring, the drop-off a solid dot (Kangaru's route ends).
    show(
      'ingo-pins',
      {
        type: 'FeatureCollection',
        features: draggable ? [] : pins.map((p) => ({ type: 'Feature', properties: { role: p.role }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })),
      },
      {
        type: 'circle',
        paint: {
          'circle-radius': ['match', ['get', 'role'], 'pickup', 6, 8],
          'circle-color': ['match', ['get', 'role'], 'pickup', '#ffffff', dropoffColour],
          'circle-stroke-color': ['match', ['get', 'role'], 'pickup', pickupColour, '#ffffff'],
          'circle-stroke-width': ['match', ['get', 'role'], 'pickup', 4, 3],
        },
      },
    );
    if (draggable && lib.current) {
      const wanted = new Set(pins.map((p) => p.role));
      for (const role of Object.keys(markers.current)) {
        if (!wanted.has(role)) {
          markers.current[role].remove();
          delete markers.current[role];
        }
      }
      for (const p of pins) {
        let marker = markers.current[p.role];
        if (!marker) {
          marker = new lib.current.Marker({ color: p.role === 'pickup' ? pickupColour : dropoffColour, draggable: true }).setLngLat([p.lng, p.lat]).addTo(instance);
          marker.on('dragend', () => {
            const at = marker.getLngLat();
            moved.current?.(p.role, at.lat, at.lng);
          });
          markers.current[p.role] = marker;
        } else {
          marker.setLngLat([p.lng, p.lat]);
        }
      }
    }

    // The phone's own dot, in a soft halo.
    const meDot = meKind ? null : me;
    show('ingo-me-halo', dot(meDot), { type: 'circle', paint: { 'circle-radius': 22, 'circle-color': colour('--ingo-secondary', '#07408f'), 'circle-opacity': 0.14 } });
    show('ingo-me', dot(meDot), { type: 'circle', paint: { 'circle-radius': 7, 'circle-color': colour('--ingo-secondary', '#07408f'), 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 } });
    // The rider's layer only: its position is the glide's (the effect below).
    if (!instance.getSource('ingo-rider')) {
      show('ingo-rider', dot(null), { type: 'circle', paint: { 'circle-radius': 9, 'circle-color': colour('--ingo-primary', '#ec6c23'), 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 } });
    }

    // Framed on everything drawn, again only when something new joins it (a
    // rider appearing, a second pin), never on every move or drag: a person
    // may be looking around.
    const points = [...pins.map((p) => [p.lng, p.lat]), ...road, ...(rider ? [[rider.lng, rider.lat]] : []), ...(pins.length === 0 && me ? [[me.lng, me.lat]] : [])];
    const key = `${pins.map((p) => p.role).join(',')}|${road.length > 0}|${Boolean(rider)}|${pins.length === 0 && Boolean(me)}`;
    frameOn.current = points.length > 0 ? points : null;
    setCanRecentre(points.length > 0);
    if (points.length > 0 && framed.current !== key) {
      // The first frame is set at once; a later one eases there.
      frameAll(instance, points, framed.current === '' ? 0 : 500, focusZoom);
      framed.current = key;
    }
  }, [ready, pins, route, rider, me, draggable, focusZoom, meKind]);

  const recentre = () => {
    if (map.current && frameOn.current) frameAll(map.current, frameOn.current, 500, focusZoom);
  };

  // The vehicles' drawings and layer, once a map has vehicles to show.
  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance || drawn === null || fleetReady) return undefined;
    let cancelled = false;
    addVehicleImages(instance).then(
      () => {
        if (cancelled || map.current !== instance) return;
        if (!instance.getSource('ingo-vehicles')) {
          instance.addSource('ingo-vehicles', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
          instance.addLayer({
            id: 'ingo-vehicles',
            type: 'symbol',
            source: 'ingo-vehicles',
            layout: {
              'icon-image': ['get', 'image'],
              'icon-rotate': ['get', 'heading'],
              'icon-rotation-alignment': 'map',
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            },
          });
        }
        setFleetReady(true);
      },
      (error) => console.warn(`[InGoMap] the vehicles could not be drawn: ${error?.message || error}`),
    );

    return () => {
      cancelled = true;
    };
  }, [ready, drawn, fleetReady]);

  // Each answer is pushed to its vehicle's track; a frame loop moves and turns
  // them while any is moving and the page is visible, then stops.
  useEffect(() => {
    const instance = map.current;
    if (!fleetReady || !instance) return;
    const source = instance.getSource('ingo-vehicles');
    if (!source) return;
    const now = performance.now();
    const seen = new Set();
    for (const v of drawn || []) {
      seen.add(v.key);
      let entry = fleet.current.get(v.key);
      if (!entry) {
        entry = { track: createTrack(), turn: null };
        fleet.current.set(v.key, entry);
      }
      entry.vehicle = v.vehicle;
      entry.state = v.state || 'free';
      entry.track.push({ lng: v.lng, lat: v.lat, at: Number.isFinite(v.at) ? v.at : Date.now() });
      if (Number.isFinite(v.heading)) entry.turn = turnTo(entry.turn, v.heading, now);
    }
    for (const key of [...fleet.current.keys()]) {
      if (!seen.has(key)) fleet.current.delete(key);
    }
    // A seam a check can read: how many riders the map draws as vehicles (not the phone's own).
    if (container.current) container.current.dataset.vehicles = String([...fleet.current.keys()].filter((key) => key !== 'me').length);

    const draw = () => {
      const t = performance.now();
      let moving = false;
      const features = [];
      for (const [key, entry] of fleet.current) {
        const at = entry.track.sample(t);
        // The same proof seam as the dot's (development builds only): where the rider is drawn.
        if (key === 'rider' && process.env.NODE_ENV !== 'production' && container.current) container.current.dataset.rider = `${at.lng.toFixed(6)},${at.lat.toFixed(6)}`;
        if (entry.track.isMoving(t) || (entry.turn && t < entry.turn.start + TURN_MS)) moving = true;
        features.push({
          type: 'Feature',
          properties: { image: vehicleImageId(entry.vehicle, entry.state), heading: headingAt(entry.turn, t) ?? 0 },
          geometry: { type: 'Point', coordinates: [at.lng, at.lat] },
        });
      }
      source.setData({ type: 'FeatureCollection', features });
      fleetFrame.current = moving && document.visibilityState !== 'hidden' ? requestAnimationFrame(draw) : 0;
    };
    if (fleetFrame.current) cancelAnimationFrame(fleetFrame.current);
    fleetFrame.current = requestAnimationFrame(draw);
  }, [fleetReady, drawn]);

  // Each new position is pushed to the track; a frame loop moves the dot
  // while the track is moving and the page is visible, then stops.
  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance) return;
    const source = instance.getSource('ingo-rider');
    if (!source) return;
    const draw = (point) => source.setData({ type: 'FeatureCollection', features: point ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [point.lng, point.lat] } }] : [] });
    // A rider drawn as a vehicle glides in the vehicles' loop instead.
    if (!rider || riderKind) {
      track.current = createTrack();
      draw(null);
      return;
    }
    track.current.push({ lng: rider.lng, lat: rider.lat, at: Number.isFinite(rider.at) ? rider.at : Date.now() });
    const tick = () => {
      const now = performance.now();
      const at = track.current.sample(now);
      draw(at);
      // A proof seam, development builds only (Kangaru's `proofSeam.ts`): a
      // check reads the dot's drawn position between two fixes.
      if (process.env.NODE_ENV !== 'production' && container.current) container.current.dataset.rider = `${at.lng.toFixed(6)},${at.lat.toFixed(6)}`;
      frame.current = track.current.isMoving(now) && document.visibilityState !== 'hidden' ? requestAnimationFrame(tick) : 0;
    };
    if (!frame.current) frame.current = requestAnimationFrame(tick);
  }, [ready, rider, riderKind]);

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      if (fleetFrame.current) cancelAnimationFrame(fleetFrame.current);
      frame.current = 0;
      fleetFrame.current = 0;
    },
    [],
  );

  if (failed) {
    return (
      <p className="ingo-map__fallback" role="note">
        The map cannot be shown on this phone. The addresses are below.
      </p>
    );
  }

  // A map one can drag pins on is a working region, not a picture.
  return (
    <div className={bleed ? 'ingo-map-frame ingo-map-frame--bleed' : 'ingo-map-frame'} style={{ height }}>
      <div ref={container} className="ingo-map" role={draggable ? 'region' : 'img'} aria-label={label} />
      {ready && canRecentre ? (
        <button type="button" className="ingo-map__recentre" aria-label="Recentre the map" onClick={recentre}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden focusable="false">
            <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" />
            <circle cx="12" cy="12" r="2.2" fill="currentColor" />
            <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

/** Everything drawn in view: one point at the focus zoom, more fitted with a margin. */
function frameAll(instance, points, duration, focusZoom = 15) {
  if (points.length === 1) {
    instance.easeTo({ center: points[0], zoom: focusZoom, duration });
    return;
  }
  const lngs = points.map((p) => p[0]);
  const lats = points.map((p) => p[1]);
  instance.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 48, maxZoom: 15, duration });
}

/** A colour from the app's palette (`src/index.css`), or its fallback before the CSS is read. */
function paletteColour(name, fallback) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

/** How long a vehicle takes to turn to a new heading (Kangaru: 400 ms). */
const TURN_MS = 400;

/** A turn from wherever the vehicle faces now to `target`, starting `now`. */
export function turnTo(turn, target, now) {
  const from = turn ? headingAt(turn, now) : ((target % 360) + 360) % 360;

  return { from, to: ((target % 360) + 360) % 360, start: now };
}

/** The heading a turn has reached at `now`: the short way round, eased in and out. */
export function headingAt(turn, now) {
  if (!turn) return null;
  const p = Math.min(1, Math.max(0, (now - turn.start) / TURN_MS));
  const delta = ((((turn.to - turn.from) % 360) + 540) % 360) - 180;
  const eased = p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2;

  return (((turn.from + delta * eased) % 360) + 360) % 360;
}

/** Kangaru's noise: building footprints, house numbers and residential shading. */
const NOISY_LAYERS = /^(housenumber|building|landuse_residential)|-housenumber|building-/;
/** Kangaru's palette: a white ground, pale water and parks, pale blue-grey roads. */
const PALETTE = { land: '#FFFFFF', water: '#CFE4F5', green: '#DCF0DC', road: '#DFE6ED', building: '#F4F6F8' };

/**
 * The basemap quietened, as Kangaru's passenger map does on load
 * (`OrderMap.tsx`, ported from its web `MapPanel.tsx`): the noisy layers
 * removed, the rest repainted by what their ids name. Place and road labels
 * stay. A layer a style does not have is skipped, never an error.
 */
export function quieten(instance) {
  const layers = instance.getStyle()?.layers || [];
  for (const layer of layers) {
    const { id, type } = layer;
    try {
      if (NOISY_LAYERS.test(id)) {
        instance.removeLayer(id);
      } else if (type === 'background') {
        instance.setPaintProperty(id, 'background-color', PALETTE.land);
      } else if (type === 'fill' && /water|ocean|sea|river/.test(id)) {
        instance.setPaintProperty(id, 'fill-color', PALETTE.water);
      } else if (type === 'fill' && /park|grass|wood|forest|scrub|pitch|golf/.test(id)) {
        instance.setPaintProperty(id, 'fill-color', PALETTE.green);
      } else if (type === 'fill' && /building/.test(id)) {
        instance.setPaintProperty(id, 'fill-color', PALETTE.building);
      } else if (/road|street|highway|motorway|bridge|tunnel|transit/.test(id)) {
        if (type === 'line') instance.setPaintProperty(id, 'line-color', PALETTE.road);
      } else if (type === 'fill' && /landcover|landuse|earth|land/.test(id)) {
        instance.setPaintProperty(id, 'fill-color', PALETTE.land);
      }
    } catch {
      // A layer the style draws in a way this cannot repaint keeps its own look.
    }
  }
}

/** The map's credit folded to its (i): still there, one tap away (OpenStreetMap's licence). */
function foldCredit(element) {
  element?.querySelector('.maplibregl-ctrl-attrib.maplibregl-compact')?.classList.remove('maplibregl-compact-show');
}

let loading = null;

/** MapLibre, loaded only by a screen that draws a map: its megabyte stays out of the app's first load. */
function loadMapLibre() {
  loading ??= Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl.css')]).then(
    ([mod]) => {
      const maplibre = mod.default ?? mod;
      // The worker from the app's own files (scripts/copy-map-worker.js): the
      // bundle hides MapLibre's own address for it, and without a worker no
      // tile is ever drawn, only the background (the emulator, 2 Oct 2026).
      maplibre.setWorkerUrl(new URL(`${process.env.PUBLIC_URL || ''}/maplibre/maplibre-gl-worker.mjs`, window.location.href).href);

      return maplibre;
    },
    (error) => {
      loading = null;
      throw error;
    },
  );

  return loading;
}
